import type { ReactNode } from 'react';
import type { ReactTable, RowData } from '@/lib/tanstack-table';
import { Input } from '@/components/ui/input';
import { FilterPopoverButton } from '@/components/common/FilterPopoverButton';
import { DataTableToolbar } from './data-table-toolbar';
import { FilterFieldsPanel } from './FilterFieldsPanel';
import {
  getActiveFilterCount,
  isAnyFieldActive,
  buildClearPatch,
  type FilterFieldConfig,
  type FilterParams,
} from './filter-field-config';

/**
 * Reusable list-page filter bar: a search input plus a config-driven
 * `FilterFieldsPanel` behind a `FilterPopoverButton`, hosted inside
 * `DataTableToolbar`. This is the shared shape behind AR Invoices/Payments/
 * Remittances/Cash Receipts — each page supplies its own `fields` config and
 * URL-search-param wiring (`params`/`onFilterChange`), since Tanstack
 * Router's `useSearch`/`navigate` are tied to a route's literal path and
 * can't be genericized here.
 */
export function ListFilterToolbar<TData extends RowData>({
  table,
  searchValue,
  onSearchChange,
  searchPlaceholder,
  fields,
  params,
  onFilterChange,
  onClearFilters,
  children,
  actions,
}: {
  table: ReactTable<TData>;
  searchValue?: string;
  onSearchChange?: (value: string) => void;
  searchPlaceholder?: string;
  fields: FilterFieldConfig[];
  params: FilterParams;
  onFilterChange: (patch: FilterParams) => void;
  onClearFilters?: () => void;
  /** Extra toolbar items rendered after the filter button (e.g. view options). */
  children?: ReactNode;
  /** Right-aligned items after the filters (e.g. sync status, primary action). */
  actions?: ReactNode;
}) {
  const activeFilterCount = getActiveFilterCount(fields, params);
  const hasCustomFilters =
    Boolean(searchValue) || isAnyFieldActive(fields, params);

  return (
    <DataTableToolbar
      table={table}
      onClearFilters={onClearFilters}
      hasCustomFilters={hasCustomFilters}
      actions={actions}
    >
      {onSearchChange && (
        <Input
          placeholder={searchPlaceholder ?? 'Search...'}
          value={searchValue ?? ''}
          onChange={(e) => onSearchChange(e.target.value)}
          className="h-8 w-[200px]"
        />
      )}
      {fields.length > 0 && (
        <FilterPopoverButton
          activeFilterCount={activeFilterCount}
          onClearAll={() => onFilterChange(buildClearPatch(fields))}
        >
          <FilterFieldsPanel
            fields={fields}
            params={params}
            onChange={onFilterChange}
          />
        </FilterPopoverButton>
      )}
      {children}
    </DataTableToolbar>
  );
}
